import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  forwardRef,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges
} from '@angular/core';
import { ControlValueAccessor, FormControl, NG_VALUE_ACCESSOR } from '@angular/forms';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, startWith, takeUntil } from 'rxjs/operators';
import { LatinStringNormalizer } from 'src/app/utils/LatinStringNormalizer';

@Component({
  selector: 'combo-search-select',
  templateUrl: './combo-search-select.component.html',
  styleUrls: ['./combo-search-select.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => ComboSearchSelectComponent),
      multi: true
    }
  ]
})
export class ComboSearchSelectComponent implements ControlValueAccessor, OnInit, OnChanges, OnDestroy {

  @Input() itens: any[] = [];
  @Input() itemId = 'id';
  @Input() itemLabel = '';
  @Input() itemDescricao = 'descricao';
  @Input() nome = 'item';
  @Input() obrigatorio = false;
  @Input() desabilitado = false;
  @Input() multiplaSelecao = false;
  @Input() placeholder = 'Selecione uma opção';
  @Input() placeholderPesquisa = 'Pesquise...';
  @Input() label = ' ';
  @Input() showDisplayId = false;
  @Input() showDisplayItemLabel = false;
  @Input() errorMessage = 'É preciso selecionar uma opção';
  @Input() mensagemSemResultados = 'Nenhum elemento encontrado.';
  @Input() textoLimpar = 'Nenhum';
  @Input() permitirLimpar = true;
  @Input() selecionarUnicoAutomaticamente = false;
  @Input() compararPor: ((primeiro: any, segundo: any) => boolean) | null = null;

  @Input()
  get valor(): any {
    return this.valorInterno;
  }
  set valor(novoValor: any) {
    this.definirValorExterno(novoValor);
  }

  @Output() valorChange = new EventEmitter<any>();

  readonly controlePesquisa = new FormControl('');

  itensFiltrados: any[] = [];
  valorInterno: any = null;
  desabilitadoPeloFormulario = false;

  private readonly destruir$ = new Subject<void>();
  private inicializado = false;
  private onChange: (valor: any) => void = () => {};
  private onTouched: () => void = () => {};

  constructor(private changeDetectorRef: ChangeDetectorRef) {
  }

  ngOnInit(): void {
    this.inicializado = true;

    this.controlePesquisa.valueChanges
      .pipe(
        startWith(''),
        debounceTime(100),
        distinctUntilChanged(),
        takeUntil(this.destruir$)
      )
      .subscribe(termo => this.filtrarItens(termo));

    this.sincronizarItens();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.itens || changes.itemId || changes.multiplaSelecao) {
      this.sincronizarItens();
    }
  }

  ngOnDestroy(): void {
    this.destruir$.next();
    this.destruir$.complete();
  }

  writeValue(valor: any): void {
    this.definirValorExterno(valor);
  }

  registerOnChange(funcao: (valor: any) => void): void {
    this.onChange = funcao;
  }

  registerOnTouched(funcao: () => void): void {
    this.onTouched = funcao;
  }

  setDisabledState(desabilitado: boolean): void {
    this.desabilitadoPeloFormulario = desabilitado;
    this.changeDetectorRef.markForCheck();
  }

  alterarValor(valor: any): void {
    this.valorInterno = this.normalizarValor(valor);
    this.onChange(this.valorInterno);
    this.valorChange.emit(this.valorInterno);
    this.changeDetectorRef.markForCheck();
  }

  marcarTodos(evento?: Event): void {
    if (evento) {
      evento.stopPropagation();
    }

    if (!this.multiplaSelecao || this.estaDesabilitado) {
      return;
    }

    this.alterarValor(this.itensFiltrados.slice());
  }

  desmarcarTodos(evento?: Event): void {
    if (evento) {
      evento.stopPropagation();
    }

    if (!this.multiplaSelecao || this.estaDesabilitado) {
      return;
    }

    this.alterarValor([]);
  }

  aoFecharPainel(aberto: boolean): void {
    if (!aberto) {
      this.onTouched();
      this.controlePesquisa.setValue('');
    }
  }

  compararItens = (primeiro: any, segundo: any): boolean => {
    if (this.compararPor) {
      return this.compararPor(primeiro, segundo);
    }

    if (primeiro === segundo) {
      return true;
    }

    if (primeiro == null || segundo == null) {
      return false;
    }

    return String(this.obterPropriedade(primeiro, this.itemId)) ===
      String(this.obterPropriedade(segundo, this.itemId));
  }

  itemToString(item: any): string {
    if (item == null) {
      return '';
    }

    const partes: any[] = [];
    const id = this.obterPropriedade(item, this.itemId);
    const descricao = this.obterPropriedade(item, this.itemDescricao);
    const itemLabel = this.obterPropriedade(item, this.itemLabel);

    if (this.showDisplayId && id != null && id !== '') {
      partes.push(id);
    }
    if (descricao != null && descricao !== '') {
      partes.push(descricao);
    }
    if (this.showDisplayItemLabel && itemLabel != null && itemLabel !== '') {
      partes.push(itemLabel);
    }

    return partes.join(' - ');
  }

  trackByItem = (indice: number, item: any): any => {
    const id = this.obterPropriedade(item, this.itemId);
    return id == null ? indice : id;
  }

  get estaDesabilitado(): boolean {
    return this.desabilitado || this.desabilitadoPeloFormulario;
  }

  get valorVazio(): boolean {
    return this.multiplaSelecao
      ? !Array.isArray(this.valorInterno) || this.valorInterno.length === 0
      : this.valorInterno == null;
  }

  get todosSelecionados(): boolean {
    const itens = this.itensFiltrados;
    const selecionados = Array.isArray(this.valorInterno) ? this.valorInterno : [];

    return itens.length > 0 &&
      itens.every(item => selecionados.some(selecionado => this.compararItens(item, selecionado)));
  }

  private filtrarItens(termo: any): void {
    const pesquisa = termo == null ? '' : String(termo).trim();
    const itens = Array.isArray(this.itens) ? this.itens : [];

    if (!pesquisa) {
      this.itensFiltrados = itens.slice();
    } else {
      this.itensFiltrados = itens.filter(item =>
        this.correspondePesquisa(this.obterPropriedade(item, this.itemDescricao), pesquisa) ||
        this.correspondePesquisa(this.obterPropriedade(item, this.itemId), pesquisa) ||
        (this.itemLabel && this.correspondePesquisa(this.obterPropriedade(item, this.itemLabel), pesquisa))
      );
    }

    this.changeDetectorRef.markForCheck();
  }

  private correspondePesquisa(valor: any, pesquisa: string): boolean {
    return valor != null &&
      LatinStringNormalizer.search({ data: String(valor), query: pesquisa });
  }

  private sincronizarItens(): void {
    this.itensFiltrados = Array.isArray(this.itens) ? this.itens.slice() : [];
    this.valorInterno = this.normalizarValor(this.valorInterno);

    if (this.inicializado &&
        this.selecionarUnicoAutomaticamente &&
        !this.multiplaSelecao &&
        this.valorInterno == null &&
        this.itensFiltrados.length === 1) {
      this.alterarValor(this.itensFiltrados[0]);
    }

    this.changeDetectorRef.markForCheck();
  }

  private definirValorExterno(valor: any): void {
    this.valorInterno = this.normalizarValor(valor);
    this.changeDetectorRef.markForCheck();
  }

  private normalizarValor(valor: any): any {
    if (this.multiplaSelecao) {
      return Array.isArray(valor) ? valor : [];
    }
    return valor == null ? null : valor;
  }

  private obterPropriedade(item: any, caminho: string): any {
    if (item == null || !caminho) {
      return undefined;
    }

    return caminho.split('.').reduce(
      (valor, propriedade) => valor == null ? undefined : valor[propriedade],
      item
    );
  }
}
